// Yoga.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// === IMAGES ===
// Replace with your actual yoga-themed hero image
import heroBg from '../assets/River/24.jpeg';  // ← change path/filename as needed
import yogaImpImg from '../assets/River/24.jpeg'; // ← change path/filename as needed
import yogaPraImg from '../assets/River/50.png'; // ← change path/filename as needed
import meditationImage from '../assets/River/52.jpeg'; // ← change path/filename as needed

// === MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.2 },
    },
};

const accordionItem = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const Yoga2 = () => {
    const { t } = useTranslation('yoga2');

    // === DATA ===
    const yogaImpDetails = [
        t('importanceText1'),
        t('importanceText2'),
        t('importanceText3')
    ];

    const yogaPraDetails = [
        t('practiceText1'),
        t('practiceText2'),
        t('practiceText3'),
        t('practiceText4')
    ];
    const dataArray = [
        {
            title: t('meditationTitle'),
            desc: t('meditationDesc')
        }
    ];

    // === STATE FOR ACCORDION ===
    const [activeIndex, setActiveIndex] = React.useState(null);

    const toggleService = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <div className="bg-[#1F1F1F] min-h-screen">
            {/* ===== HERO SECTION ===== */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[70vh] lg:h-[85vh]"
            >
                <img
                    src={heroBg}
                    alt="Yoga at Muthumuni Ayurveda River Resort"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#020202c7] via-[#00000080] to-[#42424259]" />

                <div className="container relative z-10 flex items-center justify-center pt-16 md:pt-20">
                    <div className="w-full max-w-4xl text-center">
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold leading-tight text-white font-copperplate">
                            {t('heroTitle')}
                        </h1>
                        <p className="mt-6 text-lg md:text-2xl lg:text-3xl font-semibold tracking-wide text-[#96BA33] font-lato">
                            {t('heroSubtitle')}
                        </p>



                        {/* Optional decorative SVG lines – remove if you don't like them */}
                        <div className="absolute left-8 top-10 hidden lg:block">
                            <svg width="289" height="245" viewBox="0 0 289 245" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M289 1H1V244.5" stroke="white" strokeWidth="2" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-12 right-8 hidden lg:block">
                            <svg width="289" height="244" viewBox="0 0 289 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 243.5H288V0" stroke="white" strokeWidth="2" />
                            </svg>
                        </div>
                    </div>
                </div>
            </motion.div>

            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-[#1F1F1F] py-16 md:py-24"
            >
                <div className="container mx-auto px-6 lg:px-8">
                    <h2 className="text-4xl md:text-5xl font-semibold text-center text-white mb-12 ">
                        {t('importanceTitle')}
                    </h2>

                    <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                        {/* Image on LEFT */}
                        <div>
                            <img
                                src={yogaImpImg}
                                alt={t('importanceImageAlt')}
                                className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                loading="lazy"
                            />
                        </div>

                        {/* Details on RIGHT */}
                        <div className="space-y-6 text-white text-lg leading-relaxed">
                            {yogaImpDetails.map((point, i) => (
                                <p key={i} className="font-inknut">
                                    {point}
                                </p>
                            ))}

                        </div>
                    </div>
                </div>
            </motion.section>

            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-[#1F1F1F] py-16 md:py-24"
            >
                <div className="container mx-auto px-6 lg:px-8">


                    <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
                        <div className="space-y-6 text-white text-lg leading-relaxed">
                            {yogaPraDetails.map((point, i) => (
                                <p key={i} className="font-inknut">
                                    {point}
                                </p>
                            ))}

                        </div>

                        <div>
                            <img
                                src={yogaPraImg}
                                alt={t('practiceImageAlt')}
                                className="w-full h-auto object-cover rounded-2xl shadow-2xl"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </motion.section>

            <section className="bg-[#1F1F1F] py-12">
                <div className="container font-redhat text-white">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="flex items-center gap-5"
                    >
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[15px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                            {t('spiritualTitle')}
                        </h1>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"
                    ></motion.div>

                    <div className="justify-between gap-14 lg:flex">
                        {/* Single static image – left side */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={scaleUp}
                            className="relative mb-10 h-[500px] w-full lg:mb-0 lg:h-[650px] lg:w-[40%] rounded-lg overflow-hidden"
                        >
                            <img
                                src={meditationImage}  // ← define this import at the top of your file
                                alt="Meditation and Spiritual Practice at the Resort"
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/600x650?text=Meditation+Image';
                                }}
                            />
                            {/* Optional subtle overlay for better text contrast if needed */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none"></div>
                        </motion.div>

                        {/* Services Accordion – right side */}
                        <motion.div
                            variants={staggerContainer}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.2 }}
                            className="w-full space-y-4 lg:w-[60%]"
                        >
                            {dataArray.map((item, index) => (
                                <motion.div
                                    key={index}
                                    variants={accordionItem}
                                    className="cursor-pointer rounded-lg border border-gray-600 bg-[#1a1a1a] p-4 transition hover:border-white"
                                    onClick={() => toggleService(index)}
                                    role="button"
                                    aria-expanded={activeIndex === index}
                                    aria-controls={`service-${index}`}
                                >
                                    <h2 className="flex items-center justify-between text-[16px] font-semibold lg:text-[20px]">
                                        {item.title}
                                        <span className="text-[25px]">
                                            {activeIndex === index ? '−' : '+'}
                                        </span>
                                    </h2>
                                    {activeIndex === index && (
                                        <p
                                            id={`service-${index}`}
                                            className="mt-2 text-[14px] text-[#C2C2C2] lg:text-[16px] whitespace-pre-line"
                                        >
                                            {item.desc}
                                        </p>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Yoga2;